import React, {createContext, useContext, useEffect, useState, useCallback, useMemo} from 'react';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const THEME_STORAGE_KEY = 'cad-theme-preference';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getSystemTheme = (): ResolvedTheme => {
  /* istanbul ignore next -- @preserve SSR safety check, unreachable in jsdom */
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getStoredTheme = (): Theme => {
  /* istanbul ignore next -- @preserve SSR safety check, unreachable in jsdom */
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  const validThemes: Theme[] = ['light', 'dark', 'system'];
  return validThemes.includes(stored as Theme) ? (stored as Theme) : 'system';
};

const resolveTheme = (theme: Theme): ResolvedTheme => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

const subscribeToSystemThemeChanges = (applyTheme: (theme: ResolvedTheme) => void): (() => void) | undefined => {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = () => applyTheme(getSystemTheme());

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }
  return undefined;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export const ThemeProvider = ({children, defaultTheme}: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<Theme>(() => defaultTheme ?? getStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(theme));

  const applyTheme = useCallback((newResolvedTheme: ResolvedTheme) => {
    const root = window.document.documentElement;
    const body = window.document.body;

    // Apply dark class to html element for Tailwind
    root.classList.remove('light', 'dark');
    root.classList.add(newResolvedTheme);

    // Apply background color to body for consistent dark mode
    if (newResolvedTheme === 'dark') {
      body.style.backgroundColor = '#111827'; // gray-900
      body.style.color = '#f9fafb'; // gray-50
    } else {
      body.style.backgroundColor = '#ffffff';
      body.style.color = '#111827'; // gray-900
    }

    setResolvedTheme(newResolvedTheme);
  }, []);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
      applyTheme(resolveTheme(newTheme));
    },
    [applyTheme],
  );

  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  useEffect(() => {
    applyTheme(resolveTheme(theme));
  }, [theme, applyTheme]);

  useEffect(() => {
    if (theme !== 'system') return undefined;
    /* istanbul ignore next -- @preserve SSR safety check, unreachable in jsdom */
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    return subscribeToSystemThemeChanges(applyTheme);
  }, [theme, applyTheme]);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme,
    }),
    [theme, resolvedTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
